import { Alert, Button, Message, Modal } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileCheck2, PencilLine, RefreshCw, Undo2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { canUsePermission } from "../../../lib/auth";
import { isHttpClientError } from "../../../lib/http/errors";
import { useCurrentAuthUser } from "../../auth";
import {
  buildItineraryBudgetPath,
  buildQuoteBudgetPath,
  buildCustomerQuoteEntryPath,
  buildCustomerQuotePath,
  buildItineraryEditPath,
  buildItineraryListPreviewPath,
  buildItineraryFulfillmentPath,
  getItineraryPreviewPermissions,
  canReloadAfterItineraryStatusMutation,
  canRetryItineraryPreviewLoad,
  countCustomerVisiblePendingTransports,
  fetchFinalizedBudgetQuoteVersions,
  downloadItineraryPdf,
  itineraryPreviewLoadErrorText,
  itineraryListDefaultParams,
  itineraryPreviewRuntime,
  isItineraryStatusMutationOutcomeUnknown,
  itineraryStatusMutationErrorText,
  ITINERARY_PERMISSIONS,
  notifyItinerarySales,
  parseItineraryListScope,
  type ItineraryListNavigationState,
  ItineraryDialogCloseButton,
  ItineraryCopyBar,
  ItineraryListFeaturePage,
  ItineraryOutputDrawer,
  useItineraryCopyRuntime,
  useItineraryMoveRuntime,
  ItineraryMoveBar,
} from "..";

/** 仅带 URL scope 时补齐列表导航状态，供首次进入深链初始化。 */
function buildListStateFromUrlScope(scope: NonNullable<ReturnType<typeof parseItineraryListScope>>): ItineraryListNavigationState {
  return {
    scope,
    draftItineraryIdInput: "",
    submittedItineraryId: "",
    draftKeyword: "",
    submittedKeyword: "",
    draftSchoolName: "",
    submittedSchoolName: "",
    sortBy: itineraryListDefaultParams.sortBy,
    sortOrder: itineraryListDefaultParams.sortOrder,
    page: itineraryListDefaultParams.page,
    pageSize: itineraryListDefaultParams.pageSize,
  };
}

/** 课程线路管理页面入口。 */
export function ItineraryListPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{
    type: "success" | "error";
    text: string;
    reloadLatest?: boolean;
  }>();
  const [pendingStatusAction, setPendingStatusAction] = useState<"finalize" | "unfinalize">();
  const historyListState = (location.state as { itineraryListState?: ItineraryListNavigationState } | null)
    ?.itineraryListState;
  const urlScope = parseItineraryListScope(searchParams.get("scope"));
  const initialListState = historyListState ?? (urlScope ? buildListStateFromUrlScope(urlScope) : undefined);
  const [previewReadOnly, setPreviewReadOnly] = useState(
    (initialListState?.scope ?? itineraryListDefaultParams.scope) !== "mine",
  );
  const [listScope, setListScope] = useState(initialListState?.scope ?? itineraryListDefaultParams.scope);
  const canOperatePreview = !previewReadOnly && listScope === "mine";
  const previewTriggerRef = useRef<HTMLElement | null>(null);
  const pdfOperationRef = useRef(false);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const previewItineraryId = searchParams.get("id") || searchParams.get("preview") || undefined;
  const persistListNavigationState = useCallback((listState: ItineraryListNavigationState) => {
    setListScope(listState.scope);
    if (listState.scope !== "mine") setPreviewReadOnly(true);
    const nextParams = new URLSearchParams(searchParamsRef.current);
    nextParams.set("scope", listState.scope);
    const nextSearch = nextParams.toString();
    navigate(
      { pathname: pathnameRef.current, search: nextSearch ? `?${nextSearch}` : "" },
      { replace: true, state: { itineraryListState: listState } },
    );
  }, [navigate]);
  useEffect(() => {
    setPendingStatusAction(undefined);
    setNotice(undefined);
  }, [previewItineraryId]);
  const previewItineraryIdRef = useRef(previewItineraryId);
  previewItineraryIdRef.current = previewItineraryId;
  const currentUserQuery = useCurrentAuthUser();
  const canQuote = currentUserQuery.isSuccess
    && canUsePermission(currentUserQuery.data, "opportunity.quote_budget:create");
  const canEdit = currentUserQuery.isSuccess
    && canUsePermission(currentUserQuery.data, ITINERARY_PERMISSIONS.update);
  const canFulfillment = currentUserQuery.isSuccess
    && canUsePermission(currentUserQuery.data, "fulfillment.order:create")
    && canUsePermission(currentUserQuery.data, "fulfillment.order:access");
  const { canPreview, canExport, canShare, canViewTable, canCopy, canMove, canFinalize: hasFinalizePermission }
    = getItineraryPreviewPermissions(currentUserQuery.isSuccess ? currentUserQuery.data : undefined);
  const canFinalize = canOperatePreview && hasFinalizePermission;
  const canCustomerQuote = currentUserQuery.isSuccess
    && canUsePermission(currentUserQuery.data, "opportunity.customer_quote:access");
  const canCreateCustomerQuote = currentUserQuery.isSuccess
    && canUsePermission(currentUserQuery.data, "opportunity.customer_quote:create");
  const canNotifySales = currentUserQuery.isSuccess
    && canUsePermission(currentUserQuery.data, "customization.project:access")
    && canUsePermission(currentUserQuery.data, "customization.project:notify_sales");
  const canOrderHandover = currentUserQuery.isSuccess
    && canUsePermission(currentUserQuery.data, "customization.order_handover:access");
  const copyRuntime = useItineraryCopyRuntime({
    sourceItineraryId: previewItineraryId ?? "",
    visible: canCopy && Boolean(previewItineraryId),
  });
  const queryKey = ["itinerary", "preview", previewItineraryId] as const;
  const moveRuntime = useItineraryMoveRuntime({ itineraryId: previewItineraryId ?? "", onMoved: async (moved) => {
    const refreshed = await previewQuery.refetch();
    if (previewItineraryIdRef.current !== moved.itineraryId) return;
    setNotice(refreshed.isError
      ? { type: "error", text: "行程已移动，预览刷新失败，请重新加载。", reloadLatest: true }
      : { type: "success", text: "行程及其核价、报价、确认件已移动。" });
  } });
  const previewQuery = useQuery({
    queryKey,
    queryFn: () => itineraryPreviewRuntime.getEditor(previewItineraryId ?? ""),
    enabled: canPreview && Boolean(previewItineraryId),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const dismissReturnedPreview = async (itineraryId: string) => {
    if (previewItineraryIdRef.current === itineraryId) closePreview();
    await queryClient.cancelQueries({ queryKey: ["itinerary", "preview", itineraryId] });
    await queryClient.cancelQueries({ queryKey: ["itinerary", "editor", itineraryId] });
    queryClient.removeQueries({ queryKey: ["itinerary", "preview", itineraryId] });
    queryClient.removeQueries({ queryKey: ["itinerary", "editor", itineraryId] });
    void queryClient.invalidateQueries({ queryKey: ["itinerary", "list"] });
  };
  const statusMutation = useMutation({
    mutationFn: async ({
      action,
      itineraryId,
      revision,
    }: {
      action: "finalize" | "unfinalize";
      itineraryId: string;
      revision: string;
    }) => {
      return action === "finalize"
        ? itineraryPreviewRuntime.finalize(itineraryId, revision)
        : itineraryPreviewRuntime.unfinalize(itineraryId, revision);
    },
    onSuccess: async (document, variables) => {
      if (variables.action === "unfinalize" && !currentUserQuery.data?.isSuperuser) {
        await dismissReturnedPreview(variables.itineraryId);
        Message.success("已取消定稿，行程已退回草稿；销售需等待计调重新推送后查看。");
        return;
      }
      queryClient.setQueryData(["itinerary", "preview", variables.itineraryId], document);
      queryClient.setQueryData(["itinerary", "editor", document.payload.id], document);
      void queryClient.invalidateQueries({ queryKey: ["itinerary", "list"] });
      if (previewItineraryIdRef.current === variables.itineraryId) {
        setNotice({
          type: "success",
          text: document.payload.status === "confirmed" ? "行程已定稿" : "已取消定稿，可继续编辑",
        });
      }
    },
    onError: async (error, variables) => {
      if (isItineraryStatusMutationOutcomeUnknown(error)) {
        let latest;
        try {
          latest = await itineraryPreviewRuntime.getEditor(variables.itineraryId);
          queryClient.setQueryData(
            ["itinerary", "preview", variables.itineraryId],
            latest,
          );
          queryClient.setQueryData(
            ["itinerary", "editor", variables.itineraryId],
            latest,
          );
          void queryClient.invalidateQueries({ queryKey: ["itinerary", "list"] });
        } catch (readError) {
          if (isHttpClientError(readError) && readError.code === "ITINERARY_REVIEW_REQUIRED") {
            await dismissReturnedPreview(variables.itineraryId);
            Message.info("该行程已退回草稿，请等待计调重新推送后查看。");
            return;
          }
          latest = undefined;
        }
        if (previewItineraryIdRef.current !== variables.itineraryId) return;
        const expectedStatus = variables.action === "finalize" ? "confirmed" : "draft";
        if (latest?.payload.status === expectedStatus) {
          if (variables.action === "unfinalize" && !currentUserQuery.data?.isSuperuser) {
            await dismissReturnedPreview(variables.itineraryId);
            Message.success("已核验取消定稿成功，行程已退回草稿。");
            return;
          }
          setNotice({
            type: "success",
            text: "请求响应中断，已核验服务端最新状态：操作实际已完成。",
          });
          return;
        }
        setNotice({
          type: "error",
          text: "请求响应中断，已核验服务端最新状态：操作尚未完成或仍无法确认。",
          reloadLatest: true,
        });
        return;
      }
      if (previewItineraryIdRef.current !== variables.itineraryId) return;
      setNotice({
        type: "error",
        text: itineraryStatusMutationErrorText(error, variables.action),
        reloadLatest: canReloadAfterItineraryStatusMutation(error),
      });
    },
  });
  const previewEditor = previewQuery.isError ? undefined : previewQuery.data?.payload;
  const pendingTransportCount = previewEditor
    ? countCustomerVisiblePendingTransports(previewEditor)
    : 0;
  const pendingServiceStandardCount = Object.values(previewEditor?.customerServiceStandards ?? {})
    .flat()
    .filter((item) => item.trim() === "待确认")
    .length;
  const pendingCustomerContentSummary = [
    pendingTransportCount > 0 ? `${pendingTransportCount} 条待确认交通` : undefined,
    pendingServiceStandardCount > 0 ? `${pendingServiceStandardCount} 项待确认服务标准` : undefined,
  ].filter(Boolean).join("、");
  const previewStatusMutationPending = previewQuery.isFetching || (
    statusMutation.isPending && statusMutation.variables?.itineraryId === previewEditor?.id
  );
  const mutatePreviewStatus = (action: "finalize" | "unfinalize") => {
    if (!canFinalize || !previewEditor || !previewQuery.data) return;
    statusMutation.mutate({
      action,
      itineraryId: previewEditor.id,
      revision: previewQuery.data.revision,
    });
  };
  const previewDegraded = !previewQuery.isError && previewQuery.data?.previewDegraded === true;
  const previewWarningMessage = previewDegraded
    ? previewEditor?.status === "confirmed"
      ? canFinalize
        ? `该历史行程有 ${previewQuery.data?.previewSkippedSegmentCount ?? 0} 个交通段因缺少可验证的节点引用未展示。请先取消定稿，再进入人工编辑修复；分享将使用可确认的历史内容，后台导出暂不可用。`
        : `该历史行程有 ${previewQuery.data?.previewSkippedSegmentCount ?? 0} 个交通段因缺少可验证的节点引用未展示。当前账号无取消定稿权限，请联系管理员修复；分享将使用可确认的历史内容，后台导出暂不可用。`
      : canOperatePreview && canEdit
        ? `该历史行程有 ${previewQuery.data?.previewSkippedSegmentCount ?? 0} 个交通段因缺少可验证的节点引用未展示。可进入人工编辑修复，不影响定稿；分享将使用可确认的历史内容，后台导出暂不可用。`
        : `该历史行程有 ${previewQuery.data?.previewSkippedSegmentCount ?? 0} 个交通段因缺少可验证的节点引用未展示。当前账号无编辑权限，请联系有权限的管理员修复；分享将使用可确认的历史内容，后台导出暂不可用。`
    : undefined;
  const previewErrorMessage = previewQuery.isError
    ? itineraryPreviewLoadErrorText(previewQuery.error)
    : undefined;
  const previewCanRetry = previewQuery.isError
    && canRetryItineraryPreviewLoad(previewQuery.error);
  const closePreview = () => {
    setPendingStatusAction(undefined);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("id");
      next.delete("preview");
      if (!next.get("scope")) next.set("scope", listScope);
      return next;
    }, { replace: true, state: location.state });
    setNotice(undefined);
  };
  const downloadListPdf = async (itineraryId: string, includeDayImages: boolean) => {
    if (pdfOperationRef.current) return;
    pdfOperationRef.current = true;
    try {
      const document = await itineraryPreviewRuntime.getEditor(itineraryId);
      if (document.previewDegraded) {
        throw new Error("历史行程存在未展示交通段，暂不允许导出");
      }
      await downloadItineraryPdf(itineraryId, document.payload.title || "客户行程", includeDayImages);
    } finally {
      pdfOperationRef.current = false;
    }
  };
  return (
    <>
      {previewItineraryId && currentUserQuery.isSuccess && !canPreview
        ? <Alert type="error" showIcon content="没有行程预览权限，请联系管理员授权。" /> : null}
      <ItineraryListFeaturePage
        initialState={initialListState}
        onNavigationStateChange={persistListNavigationState}
        canPreview={canPreview}
        onPreview={(itineraryId, readOnly = true) => {
          if (!canPreview) return;
          setPreviewReadOnly(readOnly);
          previewTriggerRef.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
          setPendingStatusAction(undefined);
          setNotice(undefined);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("id", itineraryId);
            next.delete("preview");
            next.set("scope", listScope);
            return next;
          }, { state: location.state });
        }}
        canQuote={canQuote}
        onQuote={(itineraryId, budgetOrderId) => navigate(
          budgetOrderId ? buildQuoteBudgetPath(budgetOrderId) : buildItineraryBudgetPath(itineraryId),
        )}
        canCustomerQuote={canCustomerQuote}
        canCreateCustomerQuote={canCreateCustomerQuote}
        onLoadFinalizedBudgetQuoteVersions={fetchFinalizedBudgetQuoteVersions}
        onCustomerQuote={(budgetOrderId, customerQuoteId) => navigate(
          customerQuoteId
            ? buildCustomerQuotePath(customerQuoteId)
            : buildCustomerQuoteEntryPath(budgetOrderId),
        )}
        canOrderHandover={canOrderHandover}
        canTicketRefund={currentUserQuery.isSuccess && canUsePermission(currentUserQuery.data, "order_handover.ticket_refund:access")}
        onTicketRefund={(teamId) => {
          window.open(`/ticket-refunds/teams/${encodeURIComponent(teamId)}`, "_blank", "noopener,noreferrer");
        }}
        canFulfillment={canFulfillment}
        onFulfillment={(itineraryId) => navigate(buildItineraryFulfillmentPath(itineraryId))}
        canEdit={canEdit}
        onEdit={(itineraryId, listState) => navigate(buildItineraryEditPath(itineraryId), {
          state: { itineraryListState: listState },
        })}
        canExport={canExport}
        onDownloadPdf={downloadListPdf}
        canNotifySales={canNotifySales}
        onNotifySales={async (projectId, itineraryId) => {
          try {
            return await notifyItinerarySales(projectId, itineraryId);
          } finally {
            // 审稿先于通知提交，发送失败或响应中断也需要刷新状态。
            void queryClient.invalidateQueries({ queryKey: ["itinerary", "list"] });
            void queryClient.invalidateQueries({ queryKey: ["itinerary", "preview", itineraryId] });
            void queryClient.invalidateQueries({ queryKey: ["itinerary", "editor", itineraryId] });
          }
        }}
      />
      <ItineraryOutputDrawer
        visible={canPreview && Boolean(previewItineraryId)}
        editor={previewEditor}
        loading={previewQuery.isLoading}
        errorMessage={previewErrorMessage}
        onRetry={previewCanRetry ? () => void previewQuery.refetch() : undefined}
        canViewTable={canViewTable}
        canExport={canExport && !previewDegraded && !previewQuery.isError}
        canShare={canShare && !previewQuery.isError}
        enableInternalInteractions
        warningMessage={previewWarningMessage}
        onStartMove={canOperatePreview && canMove
          && previewEditor?.sourceDemandId && previewEditor.status !== "archived" ? moveRuntime.open : undefined}
        onCancelMove={moveRuntime.cancel}
        movePending={moveRuntime.moving}
        moveActions={canMove && moveRuntime.active ? <ItineraryMoveBar items={moveRuntime.items}
          hasTargets={moveRuntime.hasTargets}
          selectedDemandId={moveRuntime.selectedDemandId} loading={moveRuntime.loading}
          moving={moveRuntime.moving} errorMessage={moveRuntime.errorMessage}
          onSearch={moveRuntime.search} onSelect={moveRuntime.selectDemand}
          onConfirm={moveRuntime.confirm} onCancel={moveRuntime.cancel} onRetry={moveRuntime.retry} /> : undefined}
        copyActions={canCopy && previewItineraryId ? (
          <ItineraryCopyBar
            hasSearched={copyRuntime.hasSearched}
            items={copyRuntime.items}
            selectedDemandId={copyRuntime.selectedDemandId}
            loading={copyRuntime.loading}
            copying={copyRuntime.copying}
            errorMessage={copyRuntime.errorMessage}
            onSearch={copyRuntime.submitSearch}
            onSelect={copyRuntime.selectDemand}
            onConfirm={async (replaceExisting) => {
              const copied = await copyRuntime.confirm(replaceExisting);
              navigate(canUsePermission(currentUserQuery.data, ITINERARY_PERMISSIONS.editorAccess)
                ? buildItineraryEditPath(copied.itineraryId)
                : buildItineraryListPreviewPath(copied.itineraryId), {
                state: initialListState ? { itineraryListState: initialListState } : undefined,
              });
            }}
            onRetry={copyRuntime.retry}
          />
        ) : undefined}
        lifecycleActions={canFinalize && previewEditor ? (
          <>
            {canEdit && previewDegraded && previewEditor && ["draft", "active", "reviewing"].includes(previewEditor.status) ? (
              <Button
                icon={<PencilLine size={15} />}
                onClick={() => navigate(buildItineraryEditPath(previewEditor.id), {
                  state: initialListState ? { itineraryListState: initialListState } : undefined,
                })}
              >
                进入人工编辑
              </Button>
            ) : null}
            {canFinalize && previewEditor ? (
              ["draft", "active", "reviewing"].includes(previewEditor.status) ? (
                <Button className="itinerary-finalize-button" type="primary" icon={<FileCheck2 size={15} />} loading={previewStatusMutationPending} onClick={() => setPendingStatusAction("finalize")}>定稿</Button>
              ) : previewEditor.status === "confirmed" ? (
                <Button status="warning" icon={<Undo2 size={15} />} loading={previewStatusMutationPending} onClick={() => setPendingStatusAction("unfinalize")}>取消定稿</Button>
              ) : null
            ) : null}
          </>
        ) : null}
        notice={notice ? {
          type: notice.type,
          text: notice.text,
          action: notice.reloadLatest ? (
            <Button
              size="small"
              icon={<RefreshCw size={14} />}
              loading={previewQuery.isFetching}
              onClick={() => {
                setNotice(undefined);
                void previewQuery.refetch();
              }}
            >
              重新加载最新版本
            </Button>
          ) : undefined,
        } : undefined}
        onClose={closePreview}
        afterClose={() => previewTriggerRef.current?.focus()}
      />
      <Modal
        visible={Boolean(pendingStatusAction)}
        title={pendingStatusAction === "unfinalize" ? "确认取消定稿" : "确认定稿"}
        maskStyle={{ zIndex: 1299 }}
        closeIcon={<ItineraryDialogCloseButton label={pendingStatusAction === "unfinalize" ? "关闭确认取消定稿" : "关闭确认定稿"} />}
        okText={pendingStatusAction === "unfinalize" ? "确认取消定稿" : "确认定稿"}
        okButtonProps={pendingStatusAction === "unfinalize" ? { status: "warning" } : undefined}
        cancelText="取消"
        wrapStyle={{ zIndex: 1300 }}
        onOk={() => {
          const action = pendingStatusAction;
          setPendingStatusAction(undefined);
          if (action) mutatePreviewStatus(action);
        }}
        onCancel={() => setPendingStatusAction(undefined)}
      >
        {pendingStatusAction === "unfinalize"
          ? "取消定稿后，行程将恢复为草稿并重新允许编辑。"
          : (
            <>
              {pendingCustomerContentSummary ? (
                <p className="itinerary-finalize-warning">{`当前仍有 ${pendingCustomerContentSummary}；定稿后这些内容仍会对客展示。`}</p>
              ) : null}
              <p>定稿后，行程将进入只读状态；如需继续编辑，必须先取消定稿。</p>
            </>
          )}
      </Modal>
    </>
  );
}
